import type React from 'react';
import ReactSelect, {
  components,
  type OptionProps,
  type StylesConfig,
} from 'react-select';

import { Field, type FieldProps } from 'formik';

import styles from './MultiSelectCheckboxField.module.scss';
import { mergeSelectStyles, reactSelectMenuPortalProps } from './reactSelectTheme';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectCheckboxFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  options: Option[];
  placeholder?: string;
  isDisabled?: boolean;
}

function readPx(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const CheckboxOption = (props: OptionProps<Option, true>) => {
  return (
    <components.Option {...props}>
      <div className={styles.checkRow}>
        <input
          type='checkbox'
          checked={props.isSelected}
          onChange={() => null}
          className={styles.checkInput}
        />
        <span className={styles.checkText}>{props.label}</span>
      </div>
    </components.Option>
  );
};

export const MultiSelectCheckboxField: React.FC<MultiSelectCheckboxFieldProps> = ({
  name,
  label,
  required,
  options,
  placeholder = 'Выберите...',
  isDisabled = false,
}) => {
  const labelMinWidth = options.reduce((max, option) => {
    return Math.max(max, option.label.length * 14 + 88);
  }, 168);

  const customStyles: StylesConfig<Option, true> = mergeSelectStyles<Option, true>({
    control: (_base, state) => ({
      gap: 4,
      fontSize: '18px',
      opacity: isDisabled ? 0.6 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      borderColor: state.isFocused ? '#1E88E5' : 'transparent',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e0e0e0',
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#333',
    }),
    multiValueRemove: (base) => ({
      ...base,
      ':hover': {
        backgroundColor: '#d5d5d5',
        color: '#ff0000',
      },
    }),
    // Меню не уже текста пункта: узкое поле и overflow: hidden иначе режут подпись.
    menu: () => ({
      width: 'max-content',
      minWidth: `max(100%, ${labelMinWidth}px)`,
      overflow: 'visible',
    }),
    menuPortal: (provided) => {
      const controlWidth = readPx(provided.width) ?? 0;
      const left = readPx(provided.left);
      const width = Math.max(controlWidth, labelMinWidth);
      const portalStyle: { overflow: 'visible'; width: number; minWidth: number; left?: number } = {
        overflow: 'visible',
        width,
        minWidth: width,
      };
      if (left !== null && typeof window !== 'undefined') {
        const overflowRight = left + width - (window.innerWidth - 8);
        if (overflowRight > 0) portalStyle.left = Math.max(8, left - overflowRight);
      }
      return portalStyle;
    },
    option: (_base, state) => ({
      width: 'max-content',
      minWidth: '100%',
      overflow: 'visible',
      textOverflow: 'clip',
      whiteSpace: 'nowrap',
      backgroundColor: state.isFocused ? '#f5f5f5' : 'white',
      ':active': {
        backgroundColor: '#eeeeee',
      },
    }),
  });

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => {
        const value = options.filter((option) => (field.value || []).includes(option.value));

        return (
          <div className={`form-field ${styles.root}`}>
            {label && (
              <label className='label' style={{ display: 'block', marginBottom: '8px' }}>
                <span className='labelText' style={{ fontWeight: 600 }}>
                  {label}
                </span>
                {required && <span style={{ color: 'red' }}>*</span>}
              </label>
            )}

            <ReactSelect
              options={options}
              name={field.name}
              value={value}
              onChange={(selectedOptions) => {
                if (isDisabled) return;
                const valuesArray = (selectedOptions as Option[]).map((opt) => opt.value);
                form.setFieldValue(name, valuesArray);
              }}
              onBlur={field.onBlur}
              placeholder={placeholder}
              isMulti
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              styles={customStyles}
              isSearchable={false}
              isDisabled={isDisabled}
              {...reactSelectMenuPortalProps}
              components={{
                Option: CheckboxOption,
                IndicatorSeparator: () => null,
              }}
            />

            {meta.touched && meta.error && (
              <div style={{ color: '#ff0000', fontSize: '14px', marginTop: '5px' }}>
                {meta.error}
              </div>
            )}
          </div>
        );
      }}
    </Field>
  );
};
